import axios from 'axios';

/**
 * "Has this athlete logged any season stats?"
 *
 * Ported from ios/ProZpects/Views/Profile/SeasonStatsPresence.swift. A sport's
 * stats live in whichever ledger its catalog stats_mode names — event marks for
 * track, result entries for swimming/gymnastics/rowing, a match record for
 * tennis and wrestling, the NCEA/IHSA show record for equestrian, and the
 * position/season grid for everything else — so this is a switch, not one
 * endpoint.
 *
 * That Swift file warns two copies of this switch will go stale the first time
 * a sport gains a new stats mode. Web is the third; a server-side presence
 * endpoint is the eventual fix. See the F1 design doc §8.
 *
 * NOTE: these ledgers are TOP-LEVEL routers, not under /athletes/.
 *
 * Returns null when a lookup fails, so callers can leave their current state
 * alone rather than flipping a gap block on because the network blipped.
 */

const defaultGet = (apiUrl) => (path) =>
  axios
    .get(`${apiUrl}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
    .then((r) => r.data);

const nonEmpty = (v) => Array.isArray(v) && v.length > 0;

export async function hasAnySeasonStats({ apiUrl, sportSlug, statsMode, get }) {
  const fetcher = get || defaultGet(apiUrl);
  const slug = sportSlug || '';
  const tryGet = async (path) => {
    try {
      return await fetcher(path);
    } catch {
      return undefined;
    }
  };

  // Equestrian athletes' "stats" are the NCEA/IHSA show record, whatever
  // stats_mode says — so this is checked before the mode switch.
  if (slug === 'equestrian') {
    const d = await tryGet('/equestrian-results/me');
    return d === undefined ? null : nonEmpty(d.results);
  }

  if (statsMode === 'event_marks') {
    const d = await tryGet('/event-marks/me');
    return d === undefined ? null : nonEmpty(d);
  }

  if (statsMode === 'result_entries') {
    const d = await tryGet(`/result-entries/me?sport=${encodeURIComponent(slug)}`);
    return d === undefined ? null : nonEmpty(d);
  }

  if (statsMode === 'match_record') {
    if (slug === 'tennis') {
      const d = await tryGet('/tennis-matches/me');
      return d === undefined ? null : nonEmpty(d.matches);
    }
    if (slug === 'wrestling') {
      // A wrestler's record can be matches OR placements.
      const [m, p] = await Promise.all([
        tryGet('/wrestling/matches/me'),
        tryGet('/wrestling/placements/me'),
      ]);
      if (m === undefined && p === undefined) return null;
      return nonEmpty(m && m.matches) || nonEmpty(p && p.placements);
    }
    // Don't guess a ledger for an unmapped match_record sport.
    return null;
  }

  const d = await tryGet('/game-stats/me');
  if (d === undefined) return null;
  return Array.isArray(d) && d.some((r) => String(r.stat_value || '').trim() !== '');
}

export default hasAnySeasonStats;
